import { create } from "zustand";
import { io, type Socket } from "socket.io-client";
import {
  PROTOCOL_VERSION,
  type AckResult,
  type GameSummary,
  type GameView,
} from "@gierki/shared";
import type { ApiUser } from "./api.ts";

/**
 * Cały stan klienta.
 *
 * Klient jest praktycznie bezstanowy: prawda o grze przychodzi z serwera jako
 * `GameView` po każdej zmianie. Trzymamy tu tylko ostatni widok, bufor
 * niewysłanych odpowiedzi i stan połączenia — bo te trzy rzeczy muszą przeżyć
 * utratę zasięgu.
 */

export type ConnectionStatus = "offline" | "connecting" | "online";

/** Bufor odpowiedzi na czas braku sieci: `gameId → kategoria → treść`. */
type PendingAnswers = Record<string, Record<string, string>>;

const PENDING_KEY = "gierki:pending";

function loadPending(): PendingAnswers {
  try {
    return JSON.parse(localStorage.getItem(PENDING_KEY) ?? "{}") as PendingAnswers;
  } catch {
    return {};
  }
}

function savePending(pending: PendingAnswers): void {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  } catch {
    // Prywatne okno albo pełny magazyn — bufor jest wygodą, nie warunkiem gry.
  }
}

interface State {
  user: ApiUser | null;
  socket: Socket | null;
  status: ConnectionStatus;
  /** Klient ma nieaktualną wersję protokołu i musi się przeładować. */
  outdated: boolean;

  games: GameSummary[];
  view: GameView | null;
  closed: { gameId: string; reason: string } | null;
  toast: string | null;

  /**
   * Różnica między zegarem serwera a zegarem telefonu.
   *
   * Odliczanie renderujemy lokalnie, ale liczymy je od `deadlineAt` serwera —
   * zegary telefonów w aucie bywają rozjechane o minuty, a runda musi kończyć
   * się wszystkim naraz.
   */
  clockOffsetMs: number;

  pending: PendingAnswers;

  setUser: (user: ApiUser | null) => void;
  connect: () => void;
  disconnect: () => void;
  showToast: (message: string | null) => void;

  createGame: (payload: {
    gameType: string;
    settings: unknown;
    invitedUserIds: string[];
    withCode: boolean;
  }) => Promise<string>;
  joinGame: (gameId: string) => Promise<void>;
  joinByCode: (code: string) => Promise<string>;
  leaveGame: (gameId: string) => Promise<void>;
  /** Tylko host: kasuje partię, która nigdy się nie skończy. */
  abandonGame: (gameId: string) => Promise<void>;
  sendAction: (gameId: string, action: unknown) => Promise<void>;
  /** Zapisuje odpowiedź lokalnie i próbuje wysłać. */
  answer: (gameId: string, category: string, value: string) => void;
  clearView: () => void;
}

/** Zamienia potwierdzenie z serwera na obietnicę, żeby dało się je `await`ować. */
function emitWithAck<T>(socket: Socket, event: string, payload: unknown): Promise<T> {
  return new Promise((resolve, reject) => {
    socket.timeout(15_000).emit(event, payload, (timeout: Error | null, result: AckResult<T>) => {
      if (timeout) return reject(new Error("Serwer nie odpowiada."));
      if (!result.ok) return reject(new Error(result.error));
      resolve(result.data);
    });
  });
}

export const useStore = create<State>((set, get) => ({
  user: null,
  socket: null,
  status: "offline",
  outdated: false,
  games: [],
  view: null,
  closed: null,
  toast: null,
  clockOffsetMs: 0,
  pending: loadPending(),

  setUser: (user) => set({ user }),

  connect: () => {
    if (get().socket) return;
    set({ status: "connecting" });

    const socket = io({
      // Wersja protokołu sprawdzana przed uwierzytelnieniem: zakeszowany
      // service worker po deployu dostaje jeden jasny powód do przeładowania
      // zamiast serii niezrozumiałych błędów w środku rundy.
      auth: { protocolVersion: PROTOCOL_VERSION },
      // Zasięg w aucie znika i wraca — ponawianie musi być uparte, ale
      // z rosnącym odstępem, żeby telefon w tunelu nie grzał się i nie zjadał
      // baterii na próbach co 100 ms.
      reconnection: true,
      reconnectionDelay: 500,
      reconnectionDelayMax: 5_000,
      reconnectionAttempts: Number.POSITIVE_INFINITY,
    });

    socket.on("connect", () => {
      set({ status: "online" });
      // Po powrocie z tunelu wracamy do partii i dosyłamy to, co nie doszło.
      const { view, pending } = get();
      if (view) {
        void emitWithAck(socket, "game:join", { gameId: view.id }).catch(() => undefined);
        for (const [category, value] of Object.entries(pending[view.id] ?? {})) {
          socket.emit("game:action", {
            gameId: view.id,
            action: { type: "answer", category, value },
          });
        }
      }
    });

    socket.on("disconnect", () => set({ status: "offline" }));
    socket.on("connect_error", (error: Error) => {
      // „unauthenticated" oznacza wygasłą sesję — trzeba się zalogować od nowa.
      if (error.message === "unauthenticated") set({ user: null, status: "offline" });
      else set({ status: "connecting" });
    });

    socket.on("protocol:outdated", () => set({ outdated: true }));

    socket.on("games:list", (games: GameSummary[]) => set({ games }));

    socket.on("game:view", (view: GameView) => {
      set({
        view,
        closed: null,
        clockOffsetMs: view.serverNow - Date.now(),
      });
    });

    socket.on("game:closed", (payload: { gameId: string; reason: string }) => {
      set({ closed: payload });
    });

    socket.on("game:rejected", (payload: { message: string }) => {
      set({ toast: payload.message });
    });

    set({ socket });
  },

  disconnect: () => {
    get().socket?.disconnect();
    set({ socket: null, status: "offline", view: null, games: [] });
  },

  showToast: (message) => set({ toast: message }),

  createGame: async (payload) => {
    const socket = get().socket;
    if (!socket) throw new Error("Brak połączenia.");
    const { gameId } = await emitWithAck<{ gameId: string }>(socket, "game:create", payload);
    return gameId;
  },

  joinGame: async (gameId) => {
    const socket = get().socket;
    if (!socket) throw new Error("Brak połączenia.");
    await emitWithAck(socket, "game:join", { gameId });
  },

  joinByCode: async (code) => {
    const socket = get().socket;
    if (!socket) throw new Error("Brak połączenia.");
    const { gameId } = await emitWithAck<{ gameId: string }>(socket, "game:joinByCode", { code });
    return gameId;
  },

  leaveGame: async (gameId) => {
    const socket = get().socket;
    if (!socket) throw new Error("Brak połączenia.");
    await emitWithAck(socket, "game:leave", { gameId });
    set({ view: null });
  },

  /*
   * Kasowanie idzie tą samą drogą co wyjście, ale bez zerowania widoku:
   * host kasuje z listy partii, w której właśnie nie siedzi.
   */
  abandonGame: async (gameId) => {
    const socket = get().socket;
    if (!socket) throw new Error("Brak połączenia.");
    await emitWithAck(socket, "game:abandon", { gameId });
  },

  sendAction: async (gameId, action) => {
    const socket = get().socket;
    if (!socket) throw new Error("Brak połączenia.");
    await emitWithAck(socket, "game:action", { gameId, action });
  },

  answer: (gameId, category, value) => {
    /*
     * Odpowiedź trafia najpierw do bufora w `localStorage`, dopiero potem na
     * serwer. Gdy zasięg zniknie w środku rundy, wpisane słowa przetrwają
     * i zostaną dosłane po powrocie — bez tego tunel kasuje całą rundę.
     */
    const pending = { ...get().pending };
    pending[gameId] = { ...(pending[gameId] ?? {}), [category]: value };
    set({ pending });
    savePending(pending);

    const socket = get().socket;
    if (!socket?.connected) return;

    socket.emit(
      "game:action",
      { gameId, action: { type: "answer", category, value } },
      (result: AckResult<void>) => {
        if (!result?.ok) return;
        // Serwer potwierdził — bufor przestaje być potrzebny.
        const current = { ...get().pending };
        const forGame = { ...(current[gameId] ?? {}) };
        delete forGame[category];
        if (Object.keys(forGame).length === 0) delete current[gameId];
        else current[gameId] = forGame;
        set({ pending: current });
        savePending(current);
      },
    );
  },

  clearView: () => set({ view: null, closed: null }),
}));

/** Czas serwera wyliczony z lokalnego zegara i zmierzonej różnicy. */
export function serverNow(): number {
  return Date.now() + useStore.getState().clockOffsetMs;
}
